import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useConfirmUnsaved } from "@/hooks/useUnsavedChanges";
import { DiseaseAddView } from "./DiseaseAddView";
import { DiseaseDetailView } from "./DiseaseDetailView";
import { DiseaseListView } from "./DiseaseListView";

export const DiseaseLibraryHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // The open disease is in the URL (?disease=<id>), pushed onto history, so
  // browser back from a disease returns to the list, whose search and filters
  // are in the URL too.
  const [searchParams] = useSearchParams();
  const selectedDiseaseId = searchParams.get("disease") ?? "";
  const diseaseListSearch =
    (location.state as { diseaseListSearch?: string } | null)
      ?.diseaseListSearch ?? "";
  const confirmUnsaved = useConfirmUnsaved();
  const isAddRoute = location.pathname.includes(
    "/settings/diseases/add-disease"
  );
  // Diseases can no longer be edited or deleted from the console; send old
  // bookmarks and links for those screens back to the list.
  const isRemovedRoute =
    location.pathname.includes("/settings/diseases/edit-disease") ||
    location.pathname.includes("/settings/diseases/delete-disease");

  const handleAdd = () => {
    confirmUnsaved(() => {
      navigate("/settings/diseases/add-disease", {
        state: { diseaseListSearch: location.search },
      });
    });
  };

  const handleView = (diseaseId: string) => {
    confirmUnsaved(() => {
      navigate(
        `/settings/diseases/view-diseases?disease=${encodeURIComponent(diseaseId)}`,
        {
          state: {
            diseaseListSearch: isAddRoute ? diseaseListSearch : location.search,
          },
        }
      );
    });
  };

  const handleBack = () => {
    confirmUnsaved(() => {
      navigate(`/settings/diseases/view-diseases${diseaseListSearch}`);
    });
  };

  if (isRemovedRoute) {
    return <Navigate to="/settings/diseases/view-diseases" replace />;
  }

  if (isAddRoute) {
    return (
      <div className="flex h-full flex-col">
        <DiseaseAddView onBack={handleBack} onCreated={handleView} />
      </div>
    );
  }

  if (selectedDiseaseId) {
    return (
      <div className="flex h-full flex-col">
        <DiseaseDetailView diseaseId={selectedDiseaseId} onBack={handleBack} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <DiseaseListView onView={handleView} onAdd={handleAdd} />
    </div>
  );
};
